import Flex from "@/components/atoms/Flex";
import Modal from "@/components/atoms/Modal";
import { SelectPaginateAsync } from "@/components/atoms/Select";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { formatInterlocutor } from "@/helpers/CompanyHelpers";
import { checkIfUserHasRoles } from "@/helpers/UserHelpers";
import useLoadOptions from "@/hooks/useLoadOptions";
import { CompanyCourseForm } from "@/interfaces/CompanyCourse";
import { IFilters, IPaginateResponse } from "@/interfaces/Fetch";
import { IModalChildrenProps, IModalMethods } from "@/interfaces/Modal";
import { useGetCompanyQuery } from "@/services/companies";
import { useStoreCompanyCourseMutation } from "@/services/companies/courses";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import classNames from "classnames";
import { DateTime } from "luxon";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { ToastContentProps, toast } from "react-toastify";
import StoreCourseModal from "../../course/StoreCourseModal";
import StoreInstructorModal from "../../instructors/StoreInstructorModal";

const StoreCompanyCourseModal: ForwardRefRenderFunction<
  IModalMethods,
  IModalChildrenProps<{ id: string }>
> = ({ id }, ref) => {
  const user = useSelector(selectCurrentUser);
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const { data: company } = useGetCompanyQuery(id);
  const StoreCourseModalRef = useRef<ElementRef<typeof StoreCourseModal>>(null);
  const StoreInstructorModalRef =
    useRef<ElementRef<typeof StoreInstructorModal>>(null);
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CompanyCourseForm>({
    defaultValues: {
      signatureDate: DateTime.now().toFormat("yyyy-MM-dd"),
    },
  });

  const defaultCourseAdditional = useRef<IFilters>({
    pageIndex: 0,
  }).current;

  const [storeCompanyCourseAsync] = useStoreCompanyCourseMutation();

  const onCallback = useCallback(
    (name: string, response: IPaginateResponse<any>) => {
      if (name === "loadCompanyCourseStatusesOptions" && !watch("status")) {
        setValue("status", response.data[0]);
      }
    },
    [],
  );
  const {
    loadCoursesOptions,

    loadOrganisationsOptions,
  } = useLoadOptions({ onCallback });

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({ showOrHiddenModal }));

  const onShowCourseModal = () => {
    showOrHiddenModal();
    StoreCourseModalRef.current?.showOrHiddenModal();
  };

  const onSubmit: SubmitHandler<CompanyCourseForm> = ({
    course,
    instructor,
    status,
    organisation,
    ...data
  }) => {
    toast.promise(
      storeCompanyCourseAsync([
        id,
        {
          ...data,
          statusId: status?.id,
          courseId: course?.id,
          instructorId: instructor?.id,
          organisationId: organisation?.id,
        },
      ]).unwrap(),
      {
        pending: "Création de la formation en cours...",
        success: {
          render() {
            showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                La formation à bien été ajoutée 🤩
              </p>
            );
          },
        },
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les champs que vous avez remplis semblent être incorrects.";
            } else if (data?.status === 409) {
              return "Verifiez qu'un télépro et un commercial soient bien enregistrés sur cette entreprise.";
            }

            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  const onError: SubmitErrorHandler<CompanyCourseForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <div>
      {checkIfUserHasRoles(user, ["ADMIN"]) && (
        <StoreCourseModal
          ref={StoreCourseModalRef}
          onHide={showOrHiddenModal}
        />
      )}
      <StoreInstructorModal
        ref={StoreInstructorModalRef}
        onHide={showOrHiddenModal}
      />
      <Modal
        ref={ModalRef}
        title="Ajouter une formation existante à l'entreprise"
        requiredLabel
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        <Row className="gx-3">
          <Col lg={6}>
            <Form.Group className="mb-3">
              <Flex className="flex-between-center">
                <Form.Label>
                  Formation <span className="text-danger">*</span>
                </Form.Label>
                {checkIfUserHasRoles(user, ["ADMIN"]) && (
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0"
                    onClick={onShowCourseModal}
                  >
                    Ajouter
                  </Button>
                )}
              </Flex>
              <Controller
                control={control}
                name="course"
                rules={{
                  required: "Veuillez sélectionner une formation",
                }}
                render={({ field }) => (
                  <SelectPaginateAsync
                    loadOptions={loadCoursesOptions}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id}
                    value={field.value}
                    onChange={field.onChange}
                    isClearable
                    defaultAdditional={defaultCourseAdditional}
                  />
                )}
              />
              <Form.Text className="text-muted">
                Vous devez choisir une formation qui correspond aux catégories
                du formateur
              </Form.Text>
              <Form.Control.Feedback
                type="invalid"
                className={classNames({
                  "d-block": errors.course,
                })}
              >
                {errors.course?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Organisme de formation <span className="text-danger">*</span>
              </Form.Label>
              <Controller
                control={control}
                name="organisation"
                rules={{
                  required: "Veuillez sélectionner un organisme de formation",
                }}
                render={({ field }) => (
                  <SelectPaginateAsync
                    loadOptions={loadOrganisationsOptions}
                    getOptionLabel={(option) => option.name}
                    getOptionValue={(option) => option.id.toString()}
                    value={field.value}
                    isClearable
                    onChange={field.onChange}
                  />
                )}
              />
              <Form.Control.Feedback
                type="invalid"
                className={classNames({
                  "d-block": errors.organisation,
                })}
              >
                {errors.organisation?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Type de formation</Form.Label>
              <Form.Select
                {...register("trainingType")}
                isInvalid={!!errors.trainingType}
              >
                <option value="">Sélectionner un type</option>
                <option value="presentiel">Présentiel</option>
                <option value="distanciel">Distanciel</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.trainingType?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date de signature</Form.Label>
              <Form.Control
                type="date"
                {...register("signatureDate")}
                isInvalid={!!errors.signatureDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.signatureDate?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Interlocuteur</Form.Label>
              {/* Celui de l'entreprise : une formation n'a pas d'interlocuteur à elle. */}
              <Form.Control
                readOnly
                disabled
                value={formatInterlocutor(
                company?.interlocutorFirstname,
                company?.interlocutorLastname
              ) ?? ""}
                placeholder="Aucun interlocuteur sur l'entreprise"
              />
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date de début de session</Form.Label>
              <Form.Control
                type="date"
                {...register("sessionStartDate")}
                isInvalid={!!errors.sessionStartDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.sessionStartDate?.message}
              </Form.Control.Feedback>
              <Form.Text>
                La date de début de session doit être aujourd'hui ou plus tard
              </Form.Text>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date de fin de session</Form.Label>
              <Form.Control
                type="date"
                {...register("sessionEndDate")}
                isInvalid={!!errors.sessionEndDate}
              />
              <Form.Text>
                La date de fin de session doit être aujourd'hui ou plus tard
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {errors.sessionEndDate?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre d'heures</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  placeholder="ex : 3"
                  {...register("hoursNumber")}
                  isInvalid={!!errors.hoursNumber}
                />
                <InputGroup.Text>heure(s)</InputGroup.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.hoursNumber?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Montant demandé</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  placeholder="ex : 5000"
                  {...register("askingPrice")}
                  isInvalid={!!errors.askingPrice}
                />
                <InputGroup.Text>€</InputGroup.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.askingPrice?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Modal>
    </div>
  );
};

export default forwardRef(StoreCompanyCourseModal);
